import type { RootState } from "../state";
import { getConfig } from "@/config";
import {
  HighPerformanceAudioQueue,
  type AudioQueueItem
} from "@/utils/HighPerformanceAudioQueue";
import { feedUniversalWebAudio } from "./universalBackgroundWebAudio";

// 定义Store类型（包含state和所有方法）
type LiveRoomStore = RootState & {
  [key: string]: any;
};

/**
 * 队列适配器 - 无缝集成高性能队列到现有系统
 * 保持API兼容性，同时提供高性能处理
 */

// 内部辅助函数：确保高性能队列已初始化
function ensureQueueInitialized(store: LiveRoomStore, roomId: string): boolean {
  if (!store.roomStates.has(roomId)) {
    console.error(`❌ 房间${roomId}不存在，无法初始化队列`);
    return false;
  }

  const state = store.roomStates.get(roomId);
  if (!state) {
    console.error(`❌ 房间${roomId}状态为空，无法初始化队列`);
    return false;
  }

  // 暂时禁用高性能队列，直接使用传统队列避免 Proxy 问题
  if (!state.queue) {
    state.queue = [];
  }
  return false; // 返回 false 让所有方法使用传统队列
}

// 初始化房间的高性能队列
export function initHighPerformanceQueue(this: LiveRoomStore, roomId: string) {
  if (!this.roomStates.has(roomId)) {
    console.error(`❌ 无法初始化队列：房间${roomId}不存在`);
    return;
  }

  const state = this.roomStates.get(roomId);
  if (!state) {
    console.error(`❌ 无法初始化队列：房间${roomId}状态为空`);
    return;
  }

  if (!state.highPerformanceQueue) {
    try {
      state.highPerformanceQueue = new HighPerformanceAudioQueue(10000);

      // 如果原有队列有数据，迁移到高性能队列
      if (state.queue && state.queue.length > 0) {
        state.queue.forEach(item => {
          const audioItem: AudioQueueItem = {
            id: item.id,
            text: item.text || "",
            audioUrl: item.audioUrl || "",
            status:
              item.status === "playing"
                ? "playing"
                : item.status === "played"
                  ? "completed"
                  : "pending",
            commentId: item.commentId || null,
            voiceNo: item.voiceNo,
            createTime: item.createTime || Date.now(),
            playedCharCount: item.playedCharCount || 0
          };
          state.highPerformanceQueue!.addItem(audioItem);
        });

        // 清空原队列，但保持引用以维持兼容性
        state.queue = [];
      }
    } catch (error) {
      console.error(`❌ 初始化房间${roomId}高性能队列失败:`, error);
      state.highPerformanceQueue = null;
    }
  } else {
    console.log(`✅ 房间${roomId}高性能队列已存在`);
  }
}

// 兼容性包装：添加音频到队列
export function addAudioToQueue(
  this: LiveRoomStore,
  roomId: string,
  audioData: {
    id?: string;
    text: string;
    audioUrl: string;
    status?: string;
    commentId?: string | null;
    voiceNo?: number;
    createTime?: number;
    productId?: string | null;
    playedCharCount?: number;
    // 保护期内首条新产品音频强制入队，不走任何去重
    bypassDedup?: boolean;
    // 客户端到达时间（毫秒）
    arrivalTs?: number;
  }
) {
  // 使用传统队列模式
  ensureQueueInitialized(this, roomId);

  const state = this.roomStates.get(roomId);

  if (!state) {
    console.error(`❌ 房间${roomId}状态不存在`);
    return;
  }

  // 初始化去重集与容量/过期策略
  const cfg = getConfig?.() || {};
  const capCfg =
    (cfg as any)?.Audio?.QueueCapacity ?? (cfg as any)?.AudioQueueCapacity;
  const expCfg =
    (cfg as any)?.Audio?.QueueExpiryMs ?? (cfg as any)?.AudioQueueExpiryMs;
  const maxLatencyCfg =
    (cfg as any)?.Audio?.MaxLatencyMs ?? (cfg as any)?.AudioMaxLatencyMs;
  const evictWindowCfg =
    (cfg as any)?.Audio?.EvictWindowMs ?? (cfg as any)?.AudioEvictWindowMs;
  const QUEUE_CAPACITY = Number(capCfg ?? (state as any).queueCapacity ?? 300);
  // 放宽过期时间窗口，默认30分钟；可由平台配置覆盖
  const EXPIRY_MS = Number(
    expCfg ?? (state as any).queueExpiryMs ?? 30 * 60_000
  );
  // 延迟裁剪阈值：普通条超过该时延则丢弃，默认15s
  const MAX_LATENCY_MS = Number(
    maxLatencyCfg ?? (state as any).maxLatencyMs ?? 15_000
  );
  // 容量裁剪优先移除“过老普通条”的时间窗口，默认20s
  const EVICT_WINDOW_MS = Number(
    evictWindowCfg ?? (state as any).evictWindowMs ?? 20_000
  );
  if (!(state as any).recentAudioIds) {
    (state as any).recentAudioIds = new Set<string>();
  }

  // 文本级去重（近时段同文抑制）
  const TEXT_DEDUP_MS = Number((cfg as any)?.Audio?.TextDedupMs ?? 120_000);
  if (!(state as any).recentTextKeys) {
    (state as any).recentTextKeys = new Map<string, number>();
  }
  const normalizeText = (t: string) =>
    (t || "")
      .replace(/[\u3000\s\t\r\n]+/g, " ")
      .replace(/[\uFEFF\u200B]+/g, "")
      .trim()
      .toLowerCase()
      .slice(0, 200);
  const now = Date.now();
  const inProtectWindow =
    !!(state as any).lastProductSwitchTime &&
    now - (state as any).lastProductSwitchTime <=
      Number((state as any).switchProtectMs ?? 5000);
  const textKey = normalizeText(audioData.text || "");
  const productScopedKey = `${audioData.productId || "NA"}::${textKey}`;
  const anyScopedKey = `ANY::${textKey}`;
  const recentTextKeys: Map<string, number> = (state as any).recentTextKeys;
  // 清理过期键
  try {
    for (const [k, ts] of recentTextKeys.entries()) {
      if (now - ts > TEXT_DEDUP_MS) recentTextKeys.delete(k);
    }
  } catch {}

  // 采用“到达时间优先”：不以 server createTime 判过期，不在入队时丢弃

  // 去重：相同 id 的消息忽略（修复未定义的 msgTime）
  const msgTime = audioData.createTime || Date.now();
  const incomingId = audioData.id || `${msgTime}-${Math.random()}`;
  // 复合去重键：优先按产品ID隔离，然后是版本和消息ID
  // 这样即使版本重置为1，不同产品的相同ID音频也不会被错误视为重复
  const version = state.switchVersion ?? 0;
  const pid = audioData.productId || "NA";
  const dedupKey = `${pid}::${incomingId}::${version}`;
  if (!audioData.bypassDedup && (state as any).recentAudioIds.has(dedupKey)) {
    console.warn(`🔁 丢弃重复音频: id=${incomingId}`);
    return;
  }

  const guardPid: string | null = (state as any).__switchGuardPid || null;
  let guardVoiceNo: number | null = null;
  try {
    const raw = (state as any).__switchGuardVoiceNo;
    guardVoiceNo = typeof raw === "number" && Number.isFinite(raw) ? raw : null;
  } catch {}
  const incomingPid = audioData.productId || null;
  const isUrgent = Number(audioData.voiceNo) === -1;
  const voiceNoNum = Number(audioData.voiceNo);
  const hasVoiceNo = Number.isFinite(voiceNoNum);
  if (incomingPid && hasVoiceNo) {
    let maxPlayed: number | null = null;
    try {
      for (const item of state.queue) {
        const v = Number((item as any)?.voiceNo);
        if (Number.isFinite(v) && (item as any)?.productId === incomingPid) {
          if ((item as any)?.status === "played") {
            if (maxPlayed === null || v > maxPlayed) maxPlayed = v;
          }
        }
      }
    } catch {}
  }

  // 文本级去重策略：
  // - 切换保护期内：对同文执行强去重（ANY 作用域），避免旧产品延迟消息混入
  // - 正常期：仅对同产品同文去重，降低误伤
  if (textKey && !audioData.bypassDedup) {
    // 保护期：仅当“无产品ID”时用 ANY 作用域；有产品ID则用产品作用域，避免误杀首句模板
    if (inProtectWindow && !audioData.productId) {
      if (
        recentTextKeys.has(anyScopedKey) &&
        now - (recentTextKeys.get(anyScopedKey) || 0) <= TEXT_DEDUP_MS
      ) {
        console.warn(
          `🔁 丢弃近时段同文(保护期/无产品ID): ${audioData.text.substring(0, 30)}...`
        );
        return;
      }
    } else {
      if (
        recentTextKeys.has(productScopedKey) &&
        now - (recentTextKeys.get(productScopedKey) || 0) <= TEXT_DEDUP_MS
      ) {
        console.warn(
          `🔁 丢弃近时段同产品同文: ${audioData.text.substring(0, 30)}...`
        );
        return;
      }
    }
  }
  // 延迟感知丢弃：普通条超过最大允许时延则丢弃（紧急条保留）
  try {
    const nowTs = Date.now();
    const arrival = audioData.arrivalTs || nowTs;
    // 如果服务端 createTime 与现在差异很小（≤5s）才允许作为辅助参考
    const serverTs = audioData.createTime;
    const serverDiff = serverTs ? Math.abs(nowTs - serverTs) : 0;
    const useServer = !!(serverTs && serverDiff <= 5_000);
    const baseTs = useServer ? serverTs! : arrival;
    const age = nowTs - baseTs;
    const isUrgent = audioData.voiceNo === -1;
    const qlen = state.queue ? state.queue.length : 0;
    const backlog = qlen - (state.currentIndex ?? 0);
    const queueEmpty = qlen === 0;
    const notPlaying = !state.isPlaying;
    const inProtect =
      !!(state as any).lastProductSwitchTime &&
      nowTs - (state as any).lastProductSwitchTime <=
        Number((state as any).switchProtectMs ?? 5000);
    // 动态阈值：堆积小（<6）时放宽到60s，否则使用配置阈值
    const dynamicMax =
      backlog < 6 ? Math.max(MAX_LATENCY_MS, 60_000) : MAX_LATENCY_MS;

    // 放行条件：首条/未在播/切换保护期/小队列，均不做延迟丢弃
    const bypass =
      (queueEmpty && notPlaying) || notPlaying || inProtect || qlen < 3;

    if (!isUrgent && !bypass && age > dynamicMax) {
      // console.warn(
      //   `🕒 丢弃过期普通条: age=${age}ms > ${dynamicMax}ms (useServer=${useServer}, backlog=${backlog}, qlen=${qlen}) text="${(audioData.text || "").substring(0, 30)}..."`
      // );
      return;
    } else if (!isUrgent && bypass && age > dynamicMax) {
      // console.log(
      //   `🕒 延迟条放行: 原因=${queueEmpty && notPlaying ? "startup" : inProtect ? "switchProtect" : !state.isPlaying ? "notPlaying" : "smallQueue"} age=${age}ms > ${dynamicMax}ms`
      // );
    }
  } catch {}
  // 使用传统队列
  const legacyAudioItem = {
    id: incomingId,
    text: audioData.text,
    audioUrl: audioData.audioUrl,
    status: (audioData.status === "playing"
      ? "playing"
      : audioData.status === "played"
        ? "played"
        : "pending") as "played" | "playing" | "pending",
    commentId: audioData.commentId || undefined,
    voiceNo: audioData.voiceNo,
    createTime: audioData.createTime || Date.now(),
    productId: audioData.productId || undefined,
    playedCharCount: audioData.playedCharCount || 0,
    version: state.switchVersion ?? 0,
    arrivalTs: audioData.arrivalTs || Date.now()
  };

  if (!state.queue) {
    state.queue = [];
  }
  if (audioData.voiceNo === -1) {
    try {
      const playing =
        !!state.isPlaying &&
        state.queue.length > 0 &&
        state.currentIndex >= 0 &&
        state.currentIndex < state.queue.length;

      if (!playing) {
        state.queue.unshift(legacyAudioItem);
        // 调整currentIndex，确保索引正确性
        if (state.currentIndex > 0) {
          state.currentIndex++;
        }
        // console.log(
        //   `🚨 紧急插播(队首/FIFO): curIdx=${state.currentIndex} text="${legacyAudioItem.text.substring(0, 30)}..."`
        // );
      } else {
        const cfgAny: any =
          (typeof getConfig === "function" ? getConfig() : {}) || ({} as any);
        const mode = String(
          cfgAny?.Audio?.UrgentInsertMode ||
            (state as any).urgentInsertMode ||
            "front"
        ).toLowerCase();

        // 记录插入前的队列长度，用于后续可能的currentIndex调整
        const insertPos = Math.min(
          state.queue.length,
          Math.max(0, state.currentIndex + 1)
        );

        if (mode === "fifo") {
          // 旧策略：插入到紧急段末尾（保持先来先播）
          let fifoInsertPos = insertPos;
          while (
            fifoInsertPos < state.queue.length &&
            (state.queue[fifoInsertPos] as any)?.voiceNo === -1
          )
            fifoInsertPos++;
          state.queue.splice(fifoInsertPos, 0, legacyAudioItem);
          // console.log(
          //   `🚨 紧急插播(FIFO 段插入): insertPos=${fifoInsertPos} curIdx=${state.currentIndex} text="${legacyAudioItem.text.substring(0, 30)}..."`
          // );
        } else {
          // 新策略：front（LIFO）：直接插到当前之后的第一位，最新紧急优先
          state.queue.splice(insertPos, 0, legacyAudioItem);
          // console.log(
          //   `🚨 紧急插播(Front/LIFO 插入): insertPos=${insertPos} curIdx=${state.currentIndex} text="${legacyAudioItem.text.substring(0, 30)}..."`
          // );
        }
      }
    } catch (error) {
      console.error(`❌ 紧急插播处理失败:`, error);
      // 出错时回退到简单的队尾添加，确保消息不会丢失
      state.queue.push(legacyAudioItem);
    }
  } else {
    // 普通消息：添加到队列末尾
    state.queue.push(legacyAudioItem);
    try {
      // console.log(
      //   `🧩 普通入队: index=${state.queue.length - 1} text="${legacyAudioItem.text.substring(0, 30)}..."`
      // );
    } catch {}
  }

  if (
    guardPid &&
    incomingPid &&
    incomingPid === guardPid &&
    hasVoiceNo &&
    !isUrgent
  ) {
    try {
      const prev = (state as any).__switchGuardVoiceNo;
      const prevNum = Number.isFinite(prev) ? Number(prev) : null;
      if (prevNum === null || voiceNoNum > prevNum) {
        (state as any).__switchGuardVoiceNo = voiceNoNum;
      }
    } catch {}
  }

  // 容量控制：超出容量，丢弃最早的非紧急项
  if (state.queue.length > QUEUE_CAPACITY) {
    try {
      const nowTs = Date.now();
      let removed = false;
      // 优先移除过老普通条（在当前索引之前的历史）
      for (
        let i = 0;
        i < state.queue.length && state.queue.length > QUEUE_CAPACITY;
        i++
      ) {
        const item = state.queue[i] as any;
        const isUrgent = item?.voiceNo === -1;
        const age = nowTs - (item?.arrivalTs || item?.createTime || nowTs);
        if (
          !isUrgent &&
          age > EVICT_WINDOW_MS &&
          i < (state.currentIndex ?? 0)
        ) {
          console.warn(`🗑️ 容量裁剪-移除过老普通条: idx=${i} age=${age}ms`);
          state.queue.splice(i, 1);
          // 如果移除的元素在currentIndex之前，需要调整currentIndex
          if (state.currentIndex > 0) {
            state.currentIndex--;
          }
          removed = true;
          break;
        }
      }
      if (!removed) {
        // 回退：从当前索引开始移除首个非紧急项
        const start = state.currentIndex ?? 0;
        for (let i = start; i < state.queue.length; i++) {
          if ((state.queue[i] as any).voiceNo !== -1) {
            console.warn(
              `🗑️ 容量裁剪-回退移除: idx=${i} text="${(state.queue[i] as any).text.substring(0, 30)}..."`
            );
            state.queue.splice(i, 1);
            // 如果移除的元素就是当前索引位置，需要确保currentIndex不会越界
            if (
              i === state.currentIndex &&
              state.currentIndex >= state.queue.length
            ) {
              state.currentIndex = Math.max(0, state.queue.length - 1);
            }
            break;
          }
        }
      }
    } catch (error) {
      console.error(`❌ 容量控制处理失败:`, error);
    }
  }

  try {
    const g: any = globalThis as any;
    const useUniversalWA = !!(
      g &&
      g.__universalWebAudioRooms &&
      typeof g.__universalWebAudioRooms.has === "function" &&
      g.__universalWebAudioRooms.has(roomId)
    );

    if (useUniversalWA) {
      void feedUniversalWebAudio.call(this, roomId, legacyAudioItem);
    } else if ((this as any).addToHowlerPlaylist) {
      (this as any).addToHowlerPlaylist(roomId, legacyAudioItem);
    }
  } catch {
    // 忽略预推送失败，播放路径会兜底
  }

  // 统一：重建数组引用，确保队列 UI 在所有端（含苹果）即时刷新
  try {
    state.queue = [...state.queue];
  } catch {}
}

// 兼容性包装：获取下一个音频
export function getNextAudioFromQueue(
  this: LiveRoomStore,
  roomId: string
): AudioQueueItem | null {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.error(`❌ 房间${roomId}状态不存在`);
    return null;
  }

  // 使用传统队列模式
  if (state.queue && state.currentIndex < state.queue.length) {
    const item = state.queue[state.currentIndex];
    if (item.productId != state.selectedProductId) {
      state.currentIndex++;
      return getNextAudioFromQueue.call(this, roomId);
    }

    // 🔧 调试日志：检查队列状态
    try {
      const debugQueue = state.queue.slice(0, 5).map((q: any, idx: number) => ({
        idx,
        id: q?.id,
        voiceNo: q?.voiceNo,
        pid: q?.productId || null,
        status: q?.status,
        version: q?.version,
        text: q?.text ? q.text.substring(0, 20) : null
      }));
      // console.log(
      //   `[queue-debug] roomId=${roomId} idx=${state.currentIndex} len=${state.queue.length}`,
      //   debugQueue
      // );
      // console.log(
      //   `🔍 [getNextAudioFromQueue] roomId=${roomId} currentIndex=${state.currentIndex} queueLength=${state.queue.length}`
      // );
      // console.log(
      //   `🔍 [getNextAudioFromQueue] returning item.id=${item.id} voiceNo=${item.voiceNo} text="${item.text.substring(0, 30)}..."`
      // );

      // 打印队列中前几个项目的信息
      for (let i = 0; i < Math.min(state.queue.length, 3); i++) {
        const qItem = state.queue[i];
        // console.log(
        //   `🔍 [queue[${i}]] id=${qItem.id} voiceNo=${qItem.voiceNo} text="${qItem.text.substring(0, 20)}..."`
        // );
      }
    } catch (e) {
      // console.error(`🔍 [getNextAudioFromQueue] debug error:`, e);
    }

    return {
      id: item.id,
      text: item.text || "",
      audioUrl: item.audioUrl || "",
      productId: item.productId || null,
      status:
        item.status === "playing"
          ? "playing"
          : item.status === "played"
            ? "completed"
            : "pending",
      commentId: item.commentId || null,
      voiceNo: item.voiceNo,
      createTime: item.createTime || Date.now(),
      playedCharCount: item.playedCharCount || 0
    };
  }

  return null;
}

// 兼容性：获取队列长度
export function getQueueLength(this: LiveRoomStore, roomId: string): number {
  if (!this.roomStates.has(roomId)) return 0;

  const state = this.roomStates.get(roomId);
  if (!state) return 0;

  return state.queue ? state.queue.length : 0;
}

// 兼容性：清空队列
export function clearQueue(this: LiveRoomStore, roomId: string) {
  if (!this.roomStates.has(roomId)) return;

  const state = this.roomStates.get(roomId);
  if (!state) return;

  // 使用传统队列模式
  state.queue = [];
  state.currentIndex = 0;
}

// 获取队列统计信息
export function getQueueStats(this: LiveRoomStore, roomId: string) {
  if (!this.roomStates.has(roomId)) return null;

  const state = this.roomStates.get(roomId);
  if (!state) return null;

  const queue = state.queue || [];
  const queueLength = queue.length;
  let urgentCount = 0;
  let normalCount = 0;
  let urgentPending = 0;
  let normalPending = 0;

  for (const item of queue) {
    const isUrgent = (item as any)?.voiceNo === -1;
    const isPending = (item as any)?.status !== "played";
    if (isUrgent) {
      urgentCount++;
      if (isPending) urgentPending++;
    } else {
      normalCount++;
      if (isPending) normalPending++;
    }
  }
  return {
    urgentCount,
    normalCount,
    totalProcessed: queue.filter((i: any) => i.status === "played").length,
    urgentPending,
    normalPending,
    batchPending: 0,
    totalPending: urgentPending + normalPending
  };
}

// 检查是否有紧急插播
export function hasUrgentItems(this: LiveRoomStore, roomId: string): boolean {
  if (!this.roomStates.has(roomId)) return false;

  const state = this.roomStates.get(roomId);
  if (!state) return false;

  // 传统检查
  return state.queue ? state.queue.some(item => item.voiceNo === -1) : false;
}

// 兼容性：同步队列到UI显示（传统队列模式下不需要特殊处理）
export function syncQueueForUI(this: LiveRoomStore, _roomId: string) {
  // 传统队列模式下，队列已经就是UI显示的队列，无需额外同步
}

// 新增：按 ID 增量更新队列项（避免全删全改）
export function updateQueueItem(
  this: LiveRoomStore,
  roomId: string,
  id: string,
  patch: Partial<{
    text: string;
    audioUrl: string;
    voiceNo: number;
    productId: string | null;
    playedCharCount: number;
    status: "pending" | "playing" | "played";
  }>
) {
  if (!this.roomStates.has(roomId)) return false;
  const state = this.roomStates.get(roomId);
  if (!state || !Array.isArray(state.queue)) return false;

  const idx = state.queue.findIndex((q: any) => q?.id === id);
  if (idx < 0) return false;

  const prev = state.queue[idx] as any;
  const prevVoiceNo = prev?.voiceNo;
  const nextItem = { ...prev, ...patch };
  state.queue[idx] = nextItem;

  // 若 voiceNo 发生变化，对位置做最小必要调整
  try {
    if (typeof patch.voiceNo !== "undefined" && patch.voiceNo !== prevVoiceNo) {
      const [moved] = state.queue.splice(idx, 1);
      if (!moved) return true;

      // 记录是否需要调整currentIndex
      const needAdjustIndex = idx < state.currentIndex;

      if (patch.voiceNo === -1) {
        // 变为紧急：插到“当前之后紧急段”的末尾（FIFO）
        let insertPos = Math.min(
          state.queue.length,
          Math.max(0, state.currentIndex + 1)
        );
        while (
          insertPos < state.queue.length &&
          (state.queue[insertPos] as any)?.voiceNo === -1
        )
          insertPos++;
        state.queue.splice(insertPos, 0, moved);

        // 如果元素从currentIndex之前移除，需要调整currentIndex
        if (needAdjustIndex) {
          state.currentIndex--;
        }
        // 如果新位置在currentIndex之前，也需要调整
        if (insertPos <= state.currentIndex) {
          state.currentIndex++;
        }
      } else {
        // 变为普通：简单放到末尾
        state.queue.push(moved);

        // 如果元素从currentIndex之前移除，需要调整currentIndex
        if (needAdjustIndex) {
          state.currentIndex--;
        }
      }
    }
  } catch (error) {
    console.error(`❌ 更新队列项位置调整失败:`, error);
  }
  // 统一：重建数组引用，触发响应式更新
  try {
    state.queue = [...state.queue];
  } catch {}
  return true;
}

// 组合更新：主队列按 ID 更新，同时尝试更新苹果 WA 播放器的未来项（解码成功后替换）
export async function updateQueueItemById(
  this: LiveRoomStore,
  roomId: string,
  id: string,
  patch: Partial<{
    text: string;
    audioUrl: string;
    voiceNo: number;
    productId: string | null;
  }>
) {
  const ok = updateQueueItem.call(this, roomId, id, patch);
  try {
    const hasPayload =
      typeof (patch as any)?.text === "string" ||
      typeof (patch as any)?.audioUrl === "string";
    if (ok && hasPayload && (this as any).updateAppleWAItem) {
      await (this as any).updateAppleWAItem(roomId, {
        id,
        text: (patch as any).text,
        audioUrl: (patch as any).audioUrl
      });
    }
  } catch {}
  return ok;
}
